import type { EnrollmentDetailDataResponse } from '../enrollment/enrollment-steps-service-types'
import type { AuthorizationStatusType } from './authorization-service-utils'
import type {
  ScholarshipDetails,
  ScholarshipItemAPI
} from '../scholarship/scholarship-service-types'

// ==================== ENUMS ====================

export enum AuthorizationStatusEnum {
  PENDING = -1,
  LEADER_REJECTED = 0,
  WAITING_ENROLLMENT = 1,
  ERROR_ENROLLMENT = 2,
  ONGOING = 3,
  ABANDONED = 4,
  FINISHED = 5,
  EVALUATED = 6, // DEPRECATED
  WAITING_PAYMENT = 7, // DEPRECATED
  ERROR_PAYMENT = 8, // DEPRECATED
  EXPIRED = 9,
  WAITING_REFUND = 10,
  WAITING_BOLETO = 11, // DEPRECATED
  LEADER_APPROVED = 12,
  SENT_BOLETO = 13, // DEPRECATED
  SENT_NF = 14,
  PROCESSING_REFUND = 15,
  WAITING_SECOND_APPROVER = 16,
  WAITING_BUDGET_APPROVER = 17
}

export enum CourseType {
  PRIVATE_LESSON = 'privateLesson',
  PARTNER = 'partner',
  COURSE = 'course',
  EVENT = 'event',
  BOOK = 'book',
  MENTORING = 'mentoring',
  OTHER = 'other'
}

export enum RequestType {
  MARKETPLACE = 'false',
  REFUND = 'true'
}

export enum AccountType {
  COLLABORATOR = 'COLLABORATOR',
  DEPENDENT = 'DEPENDENT'
}

// ==================== DOMAIN MODELS ====================

export interface AuthorizationStatus {
  id: number
  code: string
  name: string
  active: boolean
  group?: string
}

export interface AuthorizationStatusGroup {
  id: number
  name: string
  displayName: string
  statusIds?: number[]
  visible: boolean
  visibility?: boolean
  messageKey?: string
}

export interface AuthorizationLabel {
  id: number
  name: string
  /** Nome amigável já localizado pelo backend (ex.: "Dados bancários inválidos"). */
  displayName?: string
  backgroundColor: string
  textColor: string
  color: string
}

export interface AuthorizationLabelRelation {
  id: number
  authorizationId: number
  labelId: number
  label: AuthorizationLabel
}

export interface BankInfo {
  bankCode: string
  bankName: string
  agency: string
  account: string
  accountType: string
}

export interface TedInfo extends BankInfo {
  cpfCnpj: string
  name: string
}

export interface PixInfo {
  pixKey: string
  pixKeyType: string
}

export interface CardInfo {
  lastDigits: string
  brand: string
  holderName: string
}

export interface CourseSkill {
  id: number
  name: string
  userId: number
  courseId: number
}

// ==================== FILTER & SORT ====================

// Opção de filtro normalizada (id sempre string — ver normalizeFilterOption).
// `type` (não `interface`) para manter a index signature implícita usada por normalizeFilterOptions.
export type FilterOption = {
  id: string
  name: string
}

export type StatusFilterOption = FilterOption & {
  group?: string
}

export interface FilterOptions {
  companies: FilterOption[]
  departments: FilterOption[]
  jobs: FilterOption[]
  status: StatusFilterOption[]
  partners: FilterOption[]
  labels: FilterOption[]
  accountTypes: string[]
}

export interface SortOption {
  value: string
  label: string
}

export interface AuthorizationFilterParams {
  q?: string
  page?: number
  pageSize?: number
  startDate?: string
  endDate?: string
  createdStartDate?: string
  createdEndDate?: string
  companies?: string
  departments?: string
  jobs?: string
  status?: string
  partners?: string
  requestTypes?: RequestType
  courseTypes?: CourseType
  userTests?: boolean
  companyTests?: boolean
  labels?: string
  accountType?: AccountType
  orderBy?: string
  currentSection?: string
  authFromEmail?: number
}

// ==================== AUTHORIZATION ENTITIES ====================

export interface AuthorizationItem {
  // Identity
  id: number
  authorizationId: number

  // User Info
  userId: number
  userName: string
  userSocialName?: string
  userEmail: string
  userPhoto?: string
  userDocument: string
  userDeactivated: boolean
  accountType: AccountType
  userAccountType?: string
  isDependent: boolean
  userTestAccount: boolean

  // Company Info
  companyId: number
  companyName: string
  departmentId?: number
  departmentName?: string
  jobId?: number
  jobName?: string
  companyTestAccount: boolean

  // Course Info
  courseId: number
  courseName: string
  name: string
  subtitle?: string
  courseType: CourseType
  contentType: string
  courseLink?: string
  link?: string
  duration?: string
  flowType?: number
  productId?: number
  subtypeId?: number

  // Institution Info
  institutionId: number
  institutionName: string
  institution?: string
  institutionLogo?: string
  institutionPaymentDeadline?: string
  paymentDeadline?: string

  // Location
  city?: string
  state?: string
  country?: string

  // Pricing
  price: number
  value?: number
  originalPrice?: number
  userPaidAmount: number
  userPaid?: number
  creditsPaid: number
  coParticipationPercentage: number
  coParticipation?: number

  // Status
  statusId: number
  status?: number
  statusCode: string
  statusName: string
  currentStatus?: AuthorizationStatusType

  // Payment
  paymentType: string
  paymentTypeId?: number
  transactionId?: string
  walletId?: number
  expiresAt?: string
  sentNf: boolean
  nfUrl?: string

  // Dates
  createdAt: string
  updatedAt: string
  startDate?: string
  endDate?: string

  // Approvals
  requiresLeaderApproval: boolean
  leaderInChargeName?: string
  hadLeaderApproval: boolean
  requiresSecondApprover: boolean
  requiresBudgetApproval: boolean

  // Additional
  isRefund: boolean
  isSkillPass: boolean
  labels?: AuthorizationLabel[]
  programId?: number
  programName?: string
  type?: string

  // Scholarship
  scholarship?: ScholarshipItemAPI
}

export interface AuthorizationHistoryItem {
  id: number
  authorizationId: number
  userId: number
  userName: string
  userPhoto?: string
  comment: string
  statusId: number
  statusName: string
  isVisible: boolean
  isNf: boolean
  attachmentUrl?: string
  createdAt: string
  rating?: number
  tag?: string
}

// ==================== API RESPONSES ====================

export interface AuthorizationPaginatedResponse {
  results: AuthorizationItem[]
  pages: number
  pageSize: number
  resultsQuantity: number
}

/**
 * Envelope de GET /enrollment/v2/authorizations. O backend responde em snake_case,
 * mas o http client cameliza toda resposta (humps), então os campos chegam aqui em
 * camelCase. Mapeado para AuthorizationPaginatedResponse (domínio) em formatAuthorizationList.
 */
export interface AuthorizationListV2Response {
  page: number
  pageSize: number
  totalPages: number
  total: number
  items: AuthorizationItem[]
}

export interface AuthorizationHistoryResponse {
  authorizationHistoryList: AuthorizationHistoryItem[]
  authorizationInfo: AuthorizationDetail
  rating?: number
  payableAuthorization: boolean
}

export interface AuthorizationDetail extends AuthorizationItem, AuthorizationHistoryResponse {
  balance: number
  companyRequiresLeaderPayment: boolean
  skills?: CourseSkill[]
  rating?: number
  daysUntilEvaluation?: number
  bankInfo?: BankInfo
  tedInfo?: TedInfo
  pixInfo?: PixInfo
  payable: boolean
  // Gate da seção de pagamento SkillPass, calculado pelo backend em
  // /enrollment/v2/authorizations/{id} (individual_payment_enabled): pedido com valor +
  // matrícula confirmada + parceiro fora do mensalizado (ou anterior à virada dele).
  // Regra completa em skillhub-web/docs/SKILLPASS-INDIVIDUAL-PAYMENT-RULE.md.
  individualPaymentEnabled: boolean
  authorizationStatusEmailResendable?: boolean
  externCourse?: boolean
  card?: CardInfo
  referenceNumber?: string
  travelType?: string
  oneWayFlightDate?: string
  returnFlightDate?: string
  accommodationCheckInDate?: string
  accommodationCheckOutDate?: string
  pix?: string
  tedName?: string
  tedCpf?: string
  bankCode?: string
  bankName?: string
  bankAgency?: string
  bankAccount?: string
  enrollmentEmail?: string

  // Stored transfer data (v2 authorizations) — usados no modo "dados padrão".
  transferName?: string
  taxId?: string
  enrollmentDate?: string
  daysForPaymentPass?: number
  // Pagamento efetuado / anexos (v2 authorizations) — resumo do pagamento SkillPass.
  paymentDate?: string
  institutionPaymentAttachment?: string
  nfAttachment?: string
  // NFs/invoices já anexadas ao pedido; quando presentes, recibos não são exigidos.
  nfs?: string[]
  invoices?: string[]

  // Compliance Fields
  nfDeadline?: string
  nfComments?: string
  paymentScheduledDate?: string
  paymentProofUrl?: string
  paymentComments?: string
  trainingPaymentDate?: string
}

export interface AuthorizationPageLoaderData {
  authorizationDetail: AuthorizationDetail
  availableLabels: AuthorizationLabel[]
  requestDetails: EnrollmentDetailDataResponse
  scholarshipDetails: ScholarshipDetails | null
}

// ==================== ACTION PAYLOADS ====================

export interface ApproveRejectPayload {
  id: number
  comment?: string
  submitFromEmail?: boolean
  file?: File
}

export interface EnrollmentAcceptedPayload {
  id: number
  comment?: string
  updatedValue: number
  manualRefund: boolean
  file?: File
}

export interface SuperadminRejectedPayload {
  id: number
  comment?: string
  /** ID do motivo (tabela authorization_cancel_reason); backend espera Integer. Todo cancelamento tem motivo. */
  cancelReason: number
  file?: File
}

/** Motivo de cancelamento retornado por GET /v2/user/enrollments/{id}/cancel/reasons (já localizado pelo backend). */
export interface CancelReasonOption {
  id: number
  name: string
  rule?: string
}

/** Opção de campo customizado (cargo/departamento) de GET /management/options. */
export interface CustomFieldOption {
  value: number
  label: string
}

/** Cargos/Departamentos (campos customizados) usados nos filtros de Pedidos. */
export interface CustomFieldOptions {
  departments: CustomFieldOption[]
  jobs: CustomFieldOption[]
}

export interface ChangeStatusPayload {
  id: number
  statusId: number
  comment: string
}

export interface AddCommentPayload {
  id: number
  comment: string
  is_visible: boolean
  is_nf?: boolean
  file?: File
}

export interface UpdateLabelsPayload {
  id: number
  // Obrigatório: conjunto final de etiquetas. Lista vazia limpa todas (ação intencional).
  // Não usar opcional — evita que `undefined` vire limpeza silenciosa no backend.
  labelsId: number[]
}

// ===== SkillPass payment (legacy institution_payment/* multipart flow) =====

/**
 * Recibo fiscal (NF ou invoice) anexado no pagamento SkillPass. Campos vêm
 * "achatados" da UI; o builder monta o shape aninhado esperado pelo legado.
 * Só é exigido quando `auth.nfs` está vazio.
 */
export interface PaymentReceiptInput {
  file: File
  isInvoice: boolean
  corporateName: string
  // NF (isInvoice = false)
  nfNumber?: string
  nfValue?: string
  nfLiquidValue?: string
  cnpj?: string
  // Invoice (isInvoice = true)
  invoiceNumber?: string
  invoiceValue?: string
  invoiceCurrency?: string
}

/** Dados de transferência Stark Bank (TED manual, PIX resolvido, ou dados padrão). */
export interface InstitutionTransferData {
  name?: string
  taxId?: string
  bankCode?: string
  bankAgency?: string
  bankAccount?: string
  accountType?: string
  pix?: string
  pixType?: string
}

export interface InstitutionPaymentTransferPayload {
  id: number
  transferData: InstitutionTransferData
  receipts: PaymentReceiptInput[]
}

export interface InstitutionPaymentBoletoPayload {
  id: number
  line: string
  taxId: string
  receipts: PaymentReceiptInput[]
}

export interface InstitutionPaymentProofPayload {
  authId: number
  file: File
  value: string
  date: string // YYYY-MM-DD
  receipts: PaymentReceiptInput[]
}

/** Resposta de GET /refund/find-pix — conta resolvida a partir da chave PIX. */
export interface FindAccountByPixResponse {
  name: string
  taxId: string
  ispb: string
  branchCode: string
  accountNumber: string
  accountType: string
  bankName?: string
}

export interface SchedulePassPaymentPayload {
  id: number
  paymentDate: string
}

export interface ScheduleTrainingPaymentPayload {
  id: number
  paymentDate: string
  comment?: string
}

export interface WrongDocumentEmailPayload {
  authId: number
  customMessage: string
}

export interface LegacyGenericResponse {
  error?: boolean
  message?: string | null
}

export interface InvalidateCertificatePayload {
  authId: number
  reasonId: number | null
  comment: string
}

// ==================== CONSTANTS ====================

export const SORT_OPTIONS_DEFAULT: SortOption[] = [
  { value: 'default', label: 'Data de atualização (mais nova)' },
  { value: 'updateDate_asc', label: 'Data de atualização (mais antiga)' },
  { value: 'startDate_desc', label: 'Data de criação (mais nova)' },
  { value: 'startDate_asc', label: 'Data de criação (mais antiga)' },
  { value: 'price_desc', label: 'Maior valor' },
  { value: 'price_asc', label: 'Menor valor' },
  { value: 'institution_asc', label: 'Provedor' }
]

export const SORT_OPTIONS_SUPERADMIN: SortOption[] = [
  ...SORT_OPTIONS_DEFAULT,
  { value: 'company_asc', label: 'Empresa' },
  { value: 'institution_payment_deadline_desc', label: 'Data limite de pagamento (mais nova)' },
  { value: 'institution_payment_deadline_asc', label: 'Data limite de pagamento (mais antiga)' }
]

export const REQUEST_TYPE_OPTIONS = [
  { value: RequestType.MARKETPLACE, label: 'Marketplace' },
  { value: RequestType.REFUND, label: 'Reembolso' }
]

export const COURSE_TYPE_OPTIONS = [
  { value: CourseType.PRIVATE_LESSON, label: 'Aula Particular' },
  { value: CourseType.PARTNER, label: 'Parceiro' },
  { value: CourseType.COURSE, label: 'Curso' },
  { value: CourseType.EVENT, label: 'Evento' },
  { value: CourseType.BOOK, label: 'Livro' },
  { value: CourseType.MENTORING, label: 'Mentoria' },
  { value: CourseType.OTHER, label: 'Outro' }
]

export const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' }
]
